export function normalizeMeetingTime(value) {
  const input = String(value ?? '').trim()

  if (input === '') {
    return null
  }

  let hours
  let minutes
  let match

  if ((match = /^(\d{1,2})$/.exec(input))) {
    hours = Number(match[1])
    minutes = 0
  } else if ((match = /^(\d{1,2}):(\d{1,2})$/.exec(input))) {
    hours = Number(match[1])
    minutes = Number(match[2])
  } else if ((match = /^(\d{3,4})$/.exec(input))) {
    hours = Number(match[1].slice(0, -2))
    minutes = Number(match[1].slice(-2))
  } else {
    return null
  }

  if (hours > 23 || minutes > 59) {
    return null
  }

  return `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}`
}

export function meetingTimeOptions() {
  return Array.from({ length: 96 }, (_, index) => {
    const minutes = index * 15
    const hours = Math.floor(minutes / 60)

    return `${String(hours).padStart(2, '0')}:${String(minutes % 60).padStart(2, '0')}`
  })
}

export default function timeCombobox() {
  let input = null
  let root = null
  let model = null
  let modelListener = null
  let authoredRevision = 0
  let rangeRequest = 0
  let rangeTimer = null

  return {
    open: false,
    clientInvalid: false,
    highlightedIndex: -1,
    options: meetingTimeOptions(),

    init() {
      root = this.$root
      input = this.$refs.input ?? root?.querySelector?.('[role="combobox"]')
      model = this.$refs.model ?? root?.querySelector?.('[data-time-model]')

      if (!input || !model) {
        return
      }

      modelListener = () => this.syncFromModel()
      model.addEventListener('input', modelListener)
      this.syncFromModel()
    },

    destroy() {
      rangeRequest += 1

      if (rangeTimer !== null) {
        clearTimeout(rangeTimer)
        rangeTimer = null
      }

      if (model && modelListener) {
        model.removeEventListener('input', modelListener)
      }
    },

    syncFromModel() {
      if (!input || !model) {
        return
      }

      const raw = String(model?.value ?? '').trim()
      const normalized = normalizeMeetingTime(raw)

      input.value = normalized ?? raw
      this.clientInvalid = raw !== '' && normalized === null
    },

    openList() {
      this.open = true
      this.highlightedIndex = this.options.indexOf(normalizeMeetingTime(input.value))
      this.scrollHighlighted()
    },

    close() {
      this.open = false
      this.highlightedIndex = -1
    },

    blur() {
      this.commitTyped()
      this.close()
    },

    inputChanged(event) {
      authoredRevision += 1
      this.clientInvalid = false
      this.open = true
      this.highlightedIndex = this.options.indexOf(normalizeMeetingTime(event.target.value))
      this.scrollHighlighted()
    },

    keyDown(event) {
      if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
        event.preventDefault()

        if (!this.open) {
          this.openList()
        }

        this.move(event.key === 'ArrowDown' ? 1 : -1)

        return
      }

      if (event.key === 'Enter') {
        event.preventDefault()

        if (this.open && this.highlightedIndex >= 0) {
          this.choose(this.options[this.highlightedIndex])
        } else {
          this.commitTyped()
          this.close()
        }

        return
      }

      if (event.key === 'Escape' && this.open) {
        event.preventDefault()
        this.syncFromModel()
        this.close()
      }
    },

    move(direction) {
      if (this.options.length === 0) {
        return
      }

      if (this.highlightedIndex < 0) {
        this.highlightedIndex = direction > 0 ? 0 : this.options.length - 1
      } else {
        this.highlightedIndex = (
          this.highlightedIndex + direction + this.options.length
        ) % this.options.length
      }

      this.scrollHighlighted()
    },

    scrollHighlighted() {
      if (this.highlightedIndex < 0) {
        return
      }

      this.$nextTick(() => {
        const option = root.querySelector(
          `[data-option-index="${this.highlightedIndex}"]`
        )

        option?.scrollIntoView?.({ block: 'nearest' })
      })
    },

    choose(value) {
      if (this.commit(value)) {
        this.close()
      }
    },

    commitTyped() {
      const raw = input.value.trim()

      if (raw === '') {
        return this.commit('')
      }

      const normalized = normalizeMeetingTime(raw)

      if (normalized === null) {
        return this.syncRawInvalid(raw)
      }

      return this.commit(normalized)
    },

    commit(value, source = 'user') {
      if (source !== 'range') {
        rangeRequest += 1
      }

      const raw = String(value ?? '').trim()
      const normalized = raw === '' ? '' : normalizeMeetingTime(raw)

      if (normalized === null) {
        return this.syncRawInvalid(raw)
      }

      const previousRaw = String(model.value ?? '').trim()
      const previousValue = previousRaw === ''
        ? ''
        : (normalizeMeetingTime(previousRaw) ?? previousRaw)

      input.value = normalized
      this.clientInvalid = false

      if (model.value !== normalized) {
        model.value = normalized
        model.dispatchEvent(new Event('input', { bubbles: true }))
      }

      if (previousValue !== normalized) {
        root.dispatchEvent(new CustomEvent('meeting-time-committed', {
          bubbles: true,
          detail: {
            role: root.dataset.timeRole ?? null,
            value: normalized,
            previousValue,
            source
          }
        }))
      }

      return true
    },

    syncRawInvalid(raw) {
      this.clientInvalid = true

      if (model.value !== raw) {
        model.value = raw
        model.dispatchEvent(new Event('input', { bubbles: true }))
      }

      return false
    },

    setFromRange(event) {
      const value = event.detail?.value
      const request = ++rangeRequest
      const revision = authoredRevision

      if (rangeTimer !== null) {
        clearTimeout(rangeTimer)
      }

      // Playwright and browsers may yield between focusing/clearing a field
      // and inserting replacement text. Apply the default in a later task so
      // completed input wins through the revision check.
      rangeTimer = setTimeout(() => {
        rangeTimer = null

        if (request !== rangeRequest || revision !== authoredRevision) {
          return
        }

        if (this.commit(value, 'range') && document.activeElement === input) {
          // If insertion has not happened yet, selecting the automatic value
          // makes the imminent user/browser text replace it instead of append.
          input.select()
        }
      }, 0)
    },

    optionId(option) {
      return `${this.$root.dataset.listboxId}-${option.replace(':', '-')}`
    },

    optionSelected(option) {
      return this.options[this.highlightedIndex] === option
    },

    optionClasses(option) {
      return this.optionSelected(option)
        ? 'bg-gray-100 dark:bg-gray-800'
        : ''
    },

    activeDescendant() {
      if (!this.open || this.highlightedIndex < 0) {
        return null
      }

      return this.optionId(this.options[this.highlightedIndex])
    },

    ariaInvalid() {
      return this.clientInvalid || this.$root.dataset.serverInvalid === 'true'
    },

    validationClasses() {
      return this.clientInvalid ? 'border-red-500' : ''
    },

    chevronClasses() {
      return this.open ? 'rotate-180' : ''
    }
  }
}
